/**
 * When a print macro should run by itself, and which production days that run prints
 * for. Only the setting lives here: it is saved on the macro for the api to run
 * (fed-nutrition-api src/services/macroScheduler.ts) as whoever turned the schedule on,
 * which the api stamps on the schedule as runAs when the macro is saved.
 *
 * The productions are Mon-Wed and Thu-Sat. The days a scheduled run prints for are
 * worked out from the day it runs:
 *   current   - the production that day is in (on a Sunday, which has none, the next one)
 *   next      - the first production starting after that day
 *   afterNext - the production after that
 */
import moment from 'moment/moment';

const DATE = 'YYYY-MM-DD';

export const SCHEDULE_PRODUCTIONS = [
  {value: 'current', text: 'The production in progress that day'},
  {value: 'next', text: 'The next production'},
  {value: 'afterNext', text: 'The production after next'},
];

// moment's day numbers: 0 is Sunday
export const WEEKDAYS = [
  {value: 1, text: 'Monday'},
  {value: 2, text: 'Tuesday'},
  {value: 3, text: 'Wednesday'},
  {value: 4, text: 'Thursday'},
  {value: 5, text: 'Friday'},
  {value: 6, text: 'Saturday'},
  {value: 0, text: 'Sunday'},
];

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

/** a schedule as saved on a macro; null when the macro has never had one */
export function cleanSchedule(schedule) {
  if (!schedule || typeof schedule !== 'object') {
    return null;
  }
  const days = [...new Set((Array.isArray(schedule.days) ? schedule.days : [])
    .map(Number).filter(d => Number.isInteger(d) && d >= 0 && d <= 6))];
  return {
    enabled: !!schedule.enabled,
    days: WEEKDAYS.map(w => w.value).filter(d => days.includes(d)),
    time: TIME.test(schedule.time) ? schedule.time : '',
    // which production a run prints for; '' until one is chosen
    production: SCHEDULE_PRODUCTIONS.some(p => p.value === schedule.production) ? schedule.production : '',
    // who the api runs it as; the api stamps it, the kitchen only carries it
    runAs: schedule.runAs && schedule.runAs.id ? {id: schedule.runAs.id, email: schedule.runAs.email || ''} : null,
  };
}

/**
 * What is wrong with a schedule, in words; '' when it can be saved. Pass the macro's
 * delivery to check that too: nobody is at a browser for a scheduled run, so its pdfs
 * can only be posted to a Slack channel.
 */
export function scheduleProblem(schedule, delivery) {
  if (!schedule || !schedule.enabled) {
    return '';
  }
  if (delivery && (delivery.delivery !== 'slack' || !delivery.slackChannel)) {
    return 'a scheduled run posts to Slack: choose Post to Slack and a channel';
  }
  if (!schedule.days || schedule.days.length === 0) {
    return 'choose the days the macro runs on';
  }
  if (!TIME.test(schedule.time)) {
    return 'choose the time the macro runs at';
  }
  if (!SCHEDULE_PRODUCTIONS.some(p => p.value === schedule.production)) {
    return 'choose which production it prints for';
  }
  return '';
}

/** the production days (Mon-Wed or Thu-Sat) a date is in; [] for a Sunday */
export function productionDaysOf(date) {
  const day = moment(date, DATE);
  const weekday = day.day();
  if (weekday === 0) {
    return [];
  }
  const first = weekday <= 3 ? 1 : 4;
  return [0, 1, 2].map(i => day.clone().day(first + i).format(DATE));
}

/** the first production starting after a date */
export function productionAfter(date) {
  const day = moment(date, DATE);
  for (let i = 1; i <= 7; i++) {
    const days = productionDaysOf(day.clone().add(i, 'days'));
    if (days.length > 0 && days[0] > day.format(DATE)) {
      return days;
    }
  }
  throw new Error(`no production after ${date}`);
}

/** the production days a run on a date prints for */
export function scheduledProductionDays(runDate, production) {
  const date = moment(runDate).format(DATE);
  if (production === 'current') {
    const days = productionDaysOf(date);
    return days.length > 0 ? days : productionAfter(date);
  }
  const next = productionAfter(date);
  return production === 'afterNext' ? productionAfter(next[next.length - 1]) : next;
}

/** the next runs of a schedule after a moment in time: [{at: Date, dates: [...]}] */
export function upcomingRuns(schedule, from = new Date(), count = 3) {
  if (scheduleProblem({...schedule, enabled: true})) {
    return [];
  }
  const [hours, minutes] = schedule.time.split(':').map(Number);
  const runs = [];
  const start = moment(from);
  for (let i = 0; runs.length < count && i < 7 * count + 7; i++) {
    const at = start.clone().startOf('day').add(i, 'days').hours(hours).minutes(minutes);
    if (schedule.days.includes(at.day()) && at.isAfter(start)) {
      runs.push({at: at.toDate(), dates: scheduledProductionDays(at, schedule.production)});
    }
  }
  return runs;
}

/** e.g. 'Sun, Wed at 18:00 for the next production' */
export function describeSchedule(schedule) {
  if (!schedule || !schedule.enabled || scheduleProblem(schedule)) {
    return '';
  }
  const days = WEEKDAYS.filter(w => schedule.days.includes(w.value)).map(w => w.text.slice(0, 3)).join(', ');
  const production = SCHEDULE_PRODUCTIONS.find(p => p.value === schedule.production).text.toLowerCase();
  return `${days} at ${schedule.time} for ${production}`;
}
